import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'shared/components/ui/use-toast';
import { AssistantActionCreatePortfolioType } from 'types/AssistantTypes';
import ExcelPastOneColumns from './ExcelPastOneColumns';
import activityPanelStore from 'shared/store/activityPanelStore';
import { createLogger } from 'shared/logger';

const log = createLogger('ExcelImport');

type ExcelPastOneColumnsWrapperProps = {
  messagePortfolioCast: AssistantActionCreatePortfolioType;
  onActionSubmit: (actionId: string, threadId: string, data: any) => Promise<void>;
  onListSave?: (data: { name: string; isins: string[] }) => Promise<void>;
};

export const ExcelPastOneColumnsWrapper: React.FC<ExcelPastOneColumnsWrapperProps> = ({
  messagePortfolioCast,
  onActionSubmit,
  onListSave,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  // Intercept the submission and use our SDK logic
  const submitAction = useCallback(
    async (threadId: string, actionId: string, data: any) => {
      try {
        await onActionSubmit(actionId, threadId, data);

        toast({
          title: t('global.success'),
          description: t('assistant.setProductsSuccess'),
        });
      } catch (error) {
        log.error('action submission failed', { error });
        toast({
          title: t('global.error'),
          description: t('global.errorOccurred'),
          variant: 'destructive',
        });
        throw error;
      }
    },
    [onActionSubmit, t, toast],
  );

  // Handle list save with toast notifications
  const handleListSave = useCallback(
    async (data: { name: string; isins: string[] }) => {
      if (!onListSave) return;

      try {
        await onListSave(data);

        // Refetch product lists to update the side panel
        await activityPanelStore.getState().fetchProductLists();

        toast({
          title: t('global.success'),
          description: t('instrumentLists.addListSuccess'),
        });
      } catch (error: any) {
        log.error('product list save failed', { error });
        const detail =
          error?.data?.detail ??
          error?.message ??
          t('global.errorOccurred');
        toast({
          title: t('global.error'),
          description: detail,
          variant: 'destructive',
        });
        throw error;
      }
    },
    [onListSave, t, toast],
  );

  return (
    <ExcelPastOneColumns
      messagePortfolioCast={messagePortfolioCast}
      onSubmit={submitAction}
      onListSave={onListSave ? handleListSave : undefined}
    />
  );
};
